import type { FunctionComponent } from "react";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { swSettings } from "app/common/services/swSettings";
import BoxTitle from "pro-features/components/BoxTitle/src/BoxTitle";
import { AddToDashboard } from "app/components/dashboard/AddToDashboard/AddToDashboard";
import { DownloadButtonMenu } from "app/components/React/DownloadButtonMenu/DownloadButtonMenu";
import { TableNoData } from "app/components/React/Table/FlexTable/Big/FlexTableStatelessComponents";
import { ButtonsContainer } from "pro-features/components/Rule/src/styledComponents";
import { CHART_COLORS } from "app/constants/ChartColors";
import { i18nFilter } from "app/filters/i18nFilter";
import { percentageSignFilter } from "app/filters/ngFilters";
import type { IAgeDistribution } from "app/pages/website-analysis/demographics/types";
import { getExcelDownloadTracking, getExelLink } from "app/pages/website-analysis/demographics/utils";
import { Loader } from "app/pages/website-analysis/traffic-sources/mmx/components/common/Loader";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { PdfExportService } from "app/services/PdfExportService";
import { allTrackers } from "app/services/track/track";
import { DEFAULT_WEBSITES_DELIMITER } from "app/services/sitesService/consts";
import { FlexColumn } from "pro-features/styled components/StyledFlex/src/StyledFlex";

import Chart from "pro-features/components/Chart/src/Chart";
import { DemographicsChannelsLegends } from "../DemographicsChannelsLegends";
import { StyledLegendsWrapper } from "../StyledComponents";

import { getBarChartConfig, multipleDomainsTooltipFormatter } from "./AgeBarChartComparedConfig";
import {
    TitleContainer,
    StyledHeaderTitle,
    ChartContainer,
    ChartAndLegendsWrapper,
    ChartWrapper,
    LoaderContainer,
} from "./StyledComponents";

type IDomain = string;
export type IAgeDistributionData = Record<IDomain, IAgeDistribution>;

interface IAgeDistributionBarChartComparedProps {
    data: IAgeDistributionData;
    isLoading?: boolean;
    disableAddToDashboard?: boolean;
    downloadExcelUrl?: string;
}

export const AgeDistributionBarChartCompared: FunctionComponent<IAgeDistributionBarChartComparedProps> = (props) => {
    const { data, isLoading = false, disableAddToDashboard = false, downloadExcelUrl } = props;

    const chartRef = useRef<HTMLElement>(null);
    const params = proNavigator.getParams() as any;
    const { key } = params;
    const keys = key.split(DEFAULT_WEBSITES_DELIMITER);
    const downloadExcelPermitted = swSettings.current.resources.IsExcelAllowed;
    const apiParams = proNavigator.getApiParams(params);
    const [isEmpty, setIsEmpty] = useState(true);
    const [graphData, setGraphData] = useState([]); // data (or filtered data) for the chart
    const [fullGraphData, setFullGraphData] = useState([]); // data for the legends

    const lagendToggleHandler = useCallback(
        (filterData) => {
            setGraphData(filterData);
        },
        [graphData],
    );

    // on first load we want to set the chart and legends with the same filtered data.
    useEffect(() => {
        if (!data) {
            return;
        }

        const initGraphData = comparisonColumnChartData();
        setGraphData(initGraphData);
        setFullGraphData(initGraphData);
    }, [isLoading]);

    const comparisonColumnChartData = () => {
        let isEmptyFlag = true;
        return Object.entries(data).map(([site, data], index) => {
            return {
                name: site,
                data: Object.entries(data).map(([bucket, val]) => {
                    if (val && isEmptyFlag) {
                        // this will run only once and change the isEmpty flag if needed
                        isEmptyFlag = false;
                        setIsEmpty(false);
                    }
                    return {
                        key: bucket,
                        color: CHART_COLORS.chartMainColors[index],
                        isSite: true,
                        opacity: 1,
                        y: val,
                        dataLabels: {
                            enabled: true,
                        },
                    };
                }),
            };
        });
    };

    const getPNG = React.useCallback((): Promise<void> => {
        allTrackers.dispatchTrackEvent("Age_Distribution.png", "submit-ok", "Age_Distribution.png");
        const offSetX = 0;
        const offSetY = 50;
        const fileName = `Website Analysis Age-Distribution-(${apiParams.key})-(from: ${apiParams.from})-(to: ${apiParams.to})`;
        const styleHTML = PdfExportService.getStylesheetsForPng();
        return PdfExportService.downloadHtmlPngFedService(
            styleHTML + chartRef.current.outerHTML,
            fileName,
            chartRef.current.offsetWidth + offSetX,
            chartRef.current.offsetHeight + offSetY,
        );
    }, [chartRef]);

    const getUtilityButtons = () => {
        return (
            <ButtonsContainer>
                <DownloadButtonMenu
                    PNG={true}
                    handleDownloadFunction={getPNG}
                />
                {!disableAddToDashboard ? (
                    <AddToDashboard
                        modelType="fromWebsite"
                        webSource={"Total"}
                        type="BarChartDemographics"
                        metric={"WebDemographicsAge"}
                    />
                ) : null}

                <DownloadButtonMenu
                    Excel={true}
                    downloadUrl={downloadExcelUrl ?? getExelLink(params)}
                    excelLocked={!downloadExcelPermitted}
                    trackingFunction={getExcelDownloadTracking("Age")}
                />
            </ButtonsContainer>
        );
    };

    return (
        <ChartContainer ref={chartRef}>
            <TitleContainer>
                <FlexColumn>
                    <StyledHeaderTitle>
                        <BoxTitle tooltip={i18nFilter()("webdemographics.bar.title.tooltip")}>
                            {i18nFilter()("dashboard.metricGallery.Website.WebDemographicsAge.title")}
                        </BoxTitle>
                    </StyledHeaderTitle>
                </FlexColumn>
                {!isEmpty ? getUtilityButtons() : null}
            </TitleContainer>
            {isLoading ? (
                <LoaderContainer>
                    <Loader />
                </LoaderContainer>
            ) : (
                <>
                    {isEmpty ? (
                        <TableNoData
                            icon="no-data"
                            messageTitle={i18nFilter()("global.nodata.notavilable")}
                        />
                    ) : (
                        <ChartAndLegendsWrapper>
                            <ChartWrapper>
                                <Chart
                                    type={"column"}
                                    config={getBarChartConfig({
                                        filter: [percentageSignFilter, 1],
                                        tooltipFormatter: multipleDomainsTooltipFormatter,
                                    })}
                                    data={graphData}
                                />
                            </ChartWrapper>

                            <StyledLegendsWrapper>
                                <DemographicsChannelsLegends
                                    data={fullGraphData}
                                    keys={keys}
                                    onLegendToggle={lagendToggleHandler}
                                />
                            </StyledLegendsWrapper>
                        </ChartAndLegendsWrapper>
                    )}
                </>
            )}
        </ChartContainer>
    );
};
